import {
  Component,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  signal,
  computed,
  input,
  effect,
  EventEmitter,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartData, ChartConfig, ChartDataset, DEFAULT_CHART_CONFIG } from './chart.interfaces';
import { ChartCalculationManager } from './chart-calculation.manager';
import { ChartPathManager } from './chart-path.manager';

@Component({
  selector: 'app-svg-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './svg-chart.component.html',
  styleUrls: ['./svg-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SvgChartComponent implements OnChanges {
  // Inputs
  readonly chartData = input<ChartData>({ labels: [], datasets: [] });
  readonly config = input<ChartConfig>({});
  readonly isLoading = input<boolean>(false);
  readonly noDataMessage = input<string>('No data available');
  readonly showLegend = input<boolean>(true);

  @Output() legendClick = new EventEmitter<string>();

  // Managers
  private readonly calcManager = new ChartCalculationManager();
  private readonly pathManager = new ChartPathManager(this.calcManager);

  // Internal signals
  protected readonly yAxisValues = signal<number[]>([0, 25, 50, 75, 100]);
  protected readonly y1AxisValues = signal<number[]>([0, 25, 50, 75, 100]);

  // Computed values
  protected readonly mergedConfig = computed(() => ({
    ...DEFAULT_CHART_CONFIG,
    ...this.config()
  }));

  protected readonly chartWidth = computed(() =>
    this.calcManager.getChartWidth(this.mergedConfig())
  );

  protected readonly chartAreaHeight = computed(() =>
    this.mergedConfig().chartHeight ?? 350
  );

  protected readonly gridLineIndices = computed(() => {
    const count = this.mergedConfig().gridLineCount ?? 13;
    return Array.from({ length: count }, (_, i) => i);
  });

  protected readonly xAxisLabelTicks = computed(() => {
    const labels = this.chartData().labels ?? [];
    if (!labels.length) {
      return [] as Array<{index: number; label: string}>;
    }
    const tickMode = this.mergedConfig().xAxisTickMode ?? 'points';
    if (tickMode === 'intervals' && labels.length > 1) {
      return this.buildIntervalTicks(labels, this.mergedConfig().xAxisIntervalCount ?? 4);
    }
    // Use only real dates from data labels. Allow per-chart override of point tick count.
    const configuredPointTickCount = this.mergedConfig().xAxisPointTickCount;
    const maxTickLabels = configuredPointTickCount
      ? Math.max(2, Math.min(configuredPointTickCount, labels.length))
      : (labels.length <= 12 ? labels.length : 4);
    if (maxTickLabels >= labels.length) {
      return labels.map((label, index) => ({index, label: this.formatXAxisLabel(label)}));
    }
    const ticks: Array<{index: number; label: string}> = [];
    for (let i = 0; i < maxTickLabels; i++) {
      const idx = Math.round((i * (labels.length - 1)) / (maxTickLabels - 1));
      ticks.push({index: idx, label: this.formatXAxisLabel(labels[idx])});
    }
    return ticks.filter((tick, idx, arr) => idx === 0 || tick.index !== arr[idx - 1].index);
  });

  /** Full-width vertical bands for hover (values at each X label index). */
  protected readonly hoverBandRects = computed(() => {
    const labels = this.chartData().labels ?? [];
    const cfg = this.mergedConfig();
    const topPad = cfg.padding?.top ?? 50;
    const ch = cfg.chartHeight ?? 350;
    const leftPad = cfg.padding?.left ?? 50;
    const cw = this.chartWidth();
    const n = labels.length;
    if (n === 0) {
      return [] as Array<{ x: number; y: number; width: number; height: number }>;
    }
    if (n === 1) {
      return [{ x: leftPad, y: topPad, width: cw, height: ch }];
    }
    const step = cw / (n - 1);
    const cx = (i: number) => leftPad + i * step;
    return labels.map((_, i) => {
      const xLeft = i === 0 ? leftPad : (cx(i - 1) + cx(i)) / 2;
      const xRight = i === n - 1 ? leftPad + cw : (cx(i) + cx(i + 1)) / 2;
      return { x: xLeft, y: topPad, width: Math.max(xRight - xLeft, 2), height: ch };
    });
  });

  protected readonly hoverTooltip = signal<{
    left: number;
    top: number;
    subtitle: string;
    lines: Array<{ label: string; value: string }>;
  } | null>(null);

  protected readonly viewBoxString = computed(() => {
    const vb = this.mergedConfig().viewBox ?? { width: 800, height: 500 };
    return `0 0 ${vb.width} ${vb.height}`;
  });

  constructor() {
    effect(() => {
      const data = this.chartData();
      if (data) {
        this.yAxisValues.set(this.calcManager.calculateYAxisValues(data, 'y'));
        this.y1AxisValues.set(this.calcManager.calculateYAxisValues(data, 'y1'));
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {}


  getPathData(data: (number | null)[], dataset?: ChartDataset): string {
    return this.pathManager.getPathData(data, this.chartData(), this.mergedConfig(), dataset);
  }

  getAreaPath(dataMin: (number | null)[], dataMax: (number | null)[], dataset?: ChartDataset): string {
    return this.pathManager.getAreaPath(dataMin, dataMax, this.chartData(), this.mergedConfig(), dataset);
  }

  getGridLineX(index: number): number {
    const cfg = this.mergedConfig();
    const leftPadding = cfg.padding?.left ?? 50;
    const gridCount = cfg.gridLineCount ?? 13;
    return leftPadding + (index * (this.chartWidth() / Math.max(gridCount - 1, 1)));
  }

  getLabelX(index: number): number {
    const data = this.chartData();
    const cfg = this.mergedConfig();
    const leftPadding = cfg.padding?.left ?? 50;
    const labelCount = data.labels?.length || 1;
    return leftPadding + (index * (this.chartWidth() / Math.max(labelCount - 1, 1)));
  }

  getYLabelPosition(index: number): number {
    const cfg = this.mergedConfig();
    const topPadding = cfg.padding?.top ?? 50;
    const chartHeight = cfg.chartHeight ?? 350;
    return topPadding + 5 + (index * (chartHeight / 5));
  }

  getY1LabelPosition(index: number): number {
    const cfg = this.mergedConfig();
    const topPadding = cfg.padding?.top ?? 50;
    const chartHeight = cfg.chartHeight ?? 350;
    return topPadding + 5 + (index * (chartHeight / 5));
  }

  hasData(): boolean {
    const data = this.chartData();
    return !!(data?.labels?.length > 0 && data?.datasets?.length > 0);
  }

  hasY1Data(): boolean {
    const data = this.chartData();
    return !!(data?.datasets?.some(d => d.yAxisID === 'y1'));
  }

  getYAxisTitle(): string {
    const cfg = this.mergedConfig();
    return cfg?.scales?.y?.title?.text || cfg?.yAxisUnit || '';
  }

  getY1AxisTitle(): string {
    const cfg = this.mergedConfig();
    return cfg?.scales?.y1?.title?.text || cfg?.y1AxisUnit || '';
  }

  hasAreaData(dataset: ChartDataset): boolean {
    return !!(dataset.showArea && dataset.dataMin?.length && dataset.dataMax?.length);
  }

  pointY(dataset: ChartDataset, value: number): number {
    return this.calcManager.getYPosition(value, this.chartData(), this.mergedConfig(), dataset?.yAxisID);
  }

  pointX(dataset: ChartDataset, index: number): number {
    const cfg = this.mergedConfig();
    const leftPadding = cfg.padding?.left ?? 50;
    const chartWidth = this.chartWidth();
    const labels = this.chartData().labels ?? [];
    const useOwnLabels = dataset?.useOwnLabels && dataset?.ownLabels?.length && labels.length > 0;
    if (useOwnLabels) {
      const own = dataset.ownLabels?.[index];
      const d = own ? new Date(own) : null;
      const first = labels[0] ? new Date(labels[0]) : null;
      const last = labels[labels.length - 1] ? new Date(labels[labels.length - 1]) : null;
      if (d && first && last && !Number.isNaN(d.getTime()) && !Number.isNaN(first.getTime()) && !Number.isNaN(last.getTime())) {
        const total = last.getTime() - first.getTime();
        if (total === 0) {
          return leftPadding;
        }
        return leftPadding + (((d.getTime() - first.getTime()) / total) * chartWidth);
      }
    }
    const count = labels.length || dataset.data.length || 1;
    return leftPadding + (index * (chartWidth / Math.max(count - 1, 1)));
  }

  private formatXAxisLabel(raw: string): string {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return raw;
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}.${mm}.${yy}`;
  }

  private buildIntervalTicks(labels: string[], requestedIntervals: number): Array<{index: number; label: string}> {
    const dates = labels.map(l => new Date(l));
    const valid = dates.every(d => !Number.isNaN(d.getTime()));
    if (!valid) {
      return this.buildPointTicksFallback(labels);
    }

    const firstTs = dates[0].getTime();
    const lastTs = dates[dates.length - 1].getTime();
    if (lastTs <= firstTs) {
      return [{index: 0, label: this.formatXAxisLabel(labels[0])}];
    }

    const intervalCount = Math.max(2, Math.min(requestedIntervals, 8));
    const span = lastTs - firstTs;
    const ticks: Array<{index: number; label: string}> = [];

    for (let i = 0; i < intervalCount; i++) {
      const startTs = firstTs + (span * i) / intervalCount;
      const endTs = i === intervalCount - 1
        ? lastTs
        : firstTs + (span * (i + 1)) / intervalCount;
      const midTs = (startTs + endTs) / 2;
      const idx = this.findClosestLabelIndex(labels, midTs);
      const startText = this.formatXAxisLabel(new Date(startTs).toISOString());
      const endText = this.formatXAxisLabel(new Date(endTs).toISOString());
      ticks.push({index: idx, label: `${startText} - ${endText}`});
    }

    return ticks.filter((tick, idx, arr) => idx === 0 || tick.index !== arr[idx - 1].index);
  }

  private buildPointTicksFallback(labels: string[]): Array<{index: number; label: string}> {
    const maxTickLabels = labels.length <= 12 ? labels.length : 4;
    if (maxTickLabels >= labels.length) {
      return labels.map((label, index) => ({index, label: this.formatXAxisLabel(label)}));
    }
    const ticks: Array<{index: number; label: string}> = [];
    for (let i = 0; i < maxTickLabels; i++) {
      const idx = Math.round((i * (labels.length - 1)) / (maxTickLabels - 1));
      ticks.push({index: idx, label: this.formatXAxisLabel(labels[idx])});
    }
    return ticks.filter((tick, idx, arr) => idx === 0 || tick.index !== arr[idx - 1].index);
  }

  private findClosestLabelIndex(labels: string[], targetTs: number): number {
    let bestIdx = 0;
    let bestDelta = Number.POSITIVE_INFINITY;
    for (let i = 0; i < labels.length; i++) {
      const ts = new Date(labels[i]).getTime();
      if (Number.isNaN(ts)) {
        continue;
      }
      const delta = Math.abs(ts - targetTs);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  onHoverBandPointer(index: number, event: PointerEvent): void {
    const labels = this.chartData().labels ?? [];
    const rawDate = labels[index];
    if (rawDate == null) {
      return;
    }
    const lines: Array<{ label: string; value: string }> = [];
    for (const ds of this.chartData().datasets) {
      const v = ds.data[index];
      if (v !== null && v !== undefined && !Number.isNaN(v as number)) {
        lines.push({
          label: ds.label,
          value: typeof v === 'number' ? v.toFixed(3) : String(v)
        });
      }
    }
    this.hoverTooltip.set({
      left: event.clientX + 12,
      top: event.clientY + 12,
      subtitle: this.formatXAxisLabel(rawDate),
      lines
    });
  }

  onHoverBandLeave(): void {
    this.hoverTooltip.set(null);
  }
}
